import React, { useState, useEffect, useRef, useCallback } from 'react';
import { StyledChatButton, ChatIcon, UnreadBadge } from 'src/styled/Chat/ChatButton.styled';

interface IChatButtonProps {
  onClick: () => void;
  unreadCount?: number;
}

interface Position {
  x: number;
  y: number;
}

const STORAGE_KEY = 'chat-button-position';
const DRAG_THRESHOLD = 5; // pixels - minimum distance to consider as drag

export const ChatButton: React.FC<IChatButtonProps> = ({ onClick, unreadCount = 0 }) => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState<Position>(() => {
    // Load saved position from localStorage
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        try {
          return JSON.parse(saved);
        } catch (e) {
          console.error('Failed to parse saved position:', e);
        }
      }
    }
    return { x: 24, y: 24 }; // Default position (right: 24px, bottom: 24px)
  });
  const dragStart = useRef<Position>({ x: 0, y: 0 });
  const positionStart = useRef<Position>({ x: 0, y: 0 });
  const dragDistance = useRef(0); // Track total drag distance
  const hasDragged = useRef(false);

  // Get favicon URL from document
  const getFaviconUrl = () => {
    if (typeof window === 'undefined') return '/favicon.ico';
    const favicon = document.querySelector<HTMLLinkElement>('link[rel*="icon"]');
    return favicon?.href || '/favicon.ico';
  };

  // Save position to localStorage
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(position));
    }
  }, [position]);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    const deltaX = dragStart.current.x - e.clientX;
    const deltaY = dragStart.current.y - e.clientY;

    // Calculate total distance moved
    const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
    dragDistance.current = distance;

    // Only update position and mark as dragged if moved beyond threshold
    if (distance > DRAG_THRESHOLD) {
      hasDragged.current = true;

      const newX = Math.max(0, positionStart.current.x + deltaX);
      const newY = Math.max(0, positionStart.current.y + deltaY);

      // Prevent dragging outside viewport
      const maxX = window.innerWidth - (buttonRef.current?.offsetWidth || 60);
      const maxY = window.innerHeight - (buttonRef.current?.offsetHeight || 60);

      setPosition({
        x: Math.min(newX, maxX),
        y: Math.min(newY, maxY)
      });
    }
  }, []);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
  }, [handleMouseMove]);

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    hasDragged.current = false;
    dragDistance.current = 0; // Reset distance
    dragStart.current = { x: e.clientX, y: e.clientY };
    positionStart.current = { ...position };
    e.preventDefault();
    e.stopPropagation();

    // Attach listeners immediately for instant response
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleClick = () => {
    // Only trigger onClick if drag distance is below threshold
    if (dragDistance.current <= DRAG_THRESHOLD) {
      onClick();
    }
    // Reset state
    hasDragged.current = false;
    dragDistance.current = 0;
  };

  return (
    <StyledChatButton
      ref={buttonRef}
      onClick={handleClick}
      onMouseDown={handleMouseDown}
      aria-label="Mở trợ lý chat"
      hasUnread={unreadCount > 0}
      style={{
        right: `${position.x}px`,
        bottom: `${position.y}px`,
        cursor: isDragging ? 'grabbing' : 'grab'
      }}>
      <ChatIcon>
        <img
          src={getFaviconUrl()}
          alt="Chat"
          style={{
            width: '24px',
            height: '24px',
            objectFit: 'contain'
          }}
        />
      </ChatIcon>
      {unreadCount > 0 && <UnreadBadge>{unreadCount > 99 ? '99+' : unreadCount}</UnreadBadge>}
    </StyledChatButton>
  );
};

export default ChatButton;
